'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useNavigateWithMergedParams from '../../lib/hooks/useNavigateWithMergedParams';
import BottomSection from '@egalite/ui/IncluiPCD/JobSearch/BottomSection';
import TopSection from '@egalite/ui/IncluiPCD/JobSearch/TopSection';

const workPlaceTypeOptions = [
	{
		value: 'Presencial',
		label: 'Presencial',
	},
	{
		value: 'Trabalho Remoto Permanente',
		label: 'Remoto',
	},
	{
		value: 'Híbrido',
		label: 'Híbrido',
	},
];

const educationOptions = [
	{
		value: 'Ens. Fundamental',
		label: 'Ens. Fundamental',
	},
	{
		value: 'Ens. Médio',
		label: 'Ens. Médio',
	},
	{
		value: 'Ens. Técnico',
		label: 'Ens. Técnico',
	},
	{
		value: 'Ens. Superior',
		label: 'Ens. Superior',
	},
	{
		value: 'Pós-Graduação',
		label: 'Pós-Graduação',
	},
	{
		value: 'Mestrado',
		label: 'Mestrado',
	},
	{
		value: 'Doutorado',
		label: 'Doutorado',
	},
];

const sectorsOptions = [
	{
		value: 'Recursos Humanos',
		label: 'Recursos Humanos',
	},
	{
		value: 'Almoxarifado',
		label: 'Almoxarifado',
	},
	{
		value: 'Financeiro',
		label: 'Financeiro',
	},
	{
		value: 'Saúde',
		label: 'Saúde',
	},
	{
		value: 'Produção',
		label: 'Produção',
	},
	{
		value: 'Comercial',
		label: 'Comercial',
	},
	{
		value: 'Educação',
		label: 'Educação',
	},
	{
		value: 'Indústria',
		label: 'Indústria',
	},
	{
		value: 'Qualidade',
		label: 'Qualidade',
	},
	{
		value: 'Segurança do trabalho',
		label: 'Segurança do trabalho',
	},
	{
		value: 'Administrativo',
		label: 'Administrativo',
	},
	{
		value: 'Call center',
		label: 'Call center',
	},
	{
		value: 'Serviços gerais',
		label: 'Serviços gerais',
	},
	{
		value: 'Atendimento',
		label: 'Atendimento',
	},
	{
		value: 'Logística',
		label: 'Logística',
	},
	{
		value: 'Alimentação',
		label: 'Alimentação',
	},
	{
		value: 'Auditoria',
		label: 'Auditoria',
	},
	{
		value: 'Compliance',
		label: 'Compliance',
	},
	{
		value: 'Compras',
		label: 'Compras',
	},
	{
		value: 'Construção',
		label: 'Construção',
	},
	{
		value: 'Elétrica/eletrônica',
		label: 'Elétrica/eletrônica',
	},
	{
		value: 'Engenharia',
		label: 'Engenharia',
	},
	{
		value: 'Fiscal',
		label: 'Fiscal',
	},
	{
		value: 'Jurídico',
		label: 'Jurídico',
	},
	{
		value: 'Manutenção',
		label: 'Manutenção',
	},
	{
		value: 'Marketing',
		label: 'Marketing',
	},
	{
		value: 'Outro',
		label: 'Outro',
	},
	{
		value: 'Portaria/Segurança',
		label: 'Portaria/Segurança',
	},
	{
		value: 'Tecnologia (TI)',
		label: 'Tecnologia (TI)',
	},
];

type Props = {
	cities: Record<string, string[]>;
	jobs: any;
	sponsors?: any;
	headerOnly?: boolean;
};

const Search: FC<Props> = ({ cities: citiesProp, jobs, sponsors: sponsorsProp, headerOnly = false }) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const navigateWithParams = useNavigateWithMergedParams();
	const [filterOpen, setFilterOpen] = useState(false);
	const [sponsors, setSponsors] = useState<string[]>(searchParams.get('sponsors')?.split(',') ?? []);
	const [supporters, setSupporters] = useState<string[]>(searchParams.get('supporters')?.split(',') ?? []);
	const [workPlaces, setWorkPlaces] = useState<string[]>(searchParams.get('workPlaces')?.split(',') ?? []);
	const [educations, setEducations] = useState<string[]>(searchParams.get('educations')?.split(',') ?? []);
	const [sectors, setSectors] = useState<string[]>(searchParams.get('sectors')?.split(',') ?? []);
	const [search, setSearch] = useState(searchParams.get('searchText') ?? '');
	const [selectedState, setSelectedState] = useState<string | null>(searchParams.get('state') ?? null);
	const [selectedCity, setSelectedCity] = useState<string | null>(searchParams.get('city') ?? null);

	const pageMax = Math.ceil(jobs.count / jobs.pageSize);
	const page = Math.min(Number(searchParams.get('page')) || 1, pageMax);

	const locationDisabled = useMemo(
		() => workPlaces.length === 1 && workPlaces.includes('Trabalho Remoto Permanente'),
		[workPlaces],
	);

	const states = useMemo(
		() =>
			Object.keys(citiesProp?.data ?? {}).map(state => ({
				value: state,
				label: state.split(':')[1],
			})),
		[citiesProp],
	);

	const cities = useMemo(
		() =>
			(citiesProp?.data ?? {})[selectedState ?? '']?.map(city => ({
				value: city,
				label: city,
			})) ?? [],
		[citiesProp, selectedState],
	);

	useEffect(() => {
		if (locationDisabled) setSelectedCity(null);
	}, [locationDisabled]);

	const onSearch = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			navigateWithParams(
				headerOnly ? '/busca-de-vagas' : pathname,
				{
					page: '1',
					searchText: search,
					state: !locationDisabled && selectedState ? selectedState : '',
					city: !locationDisabled && selectedCity ? selectedCity : '',
					workPlaces: workPlaces.join(','),
					educations: educations.join(','),
					sectors: sectors.join(','),
					sponsors: sponsors.join(','),
					supporters: supporters.join(','),
				},
				headerOnly ? 'push' : 'replace',
				headerOnly ? 'results' : null,
			);
		},
		[
			navigateWithParams,
			headerOnly,
			pathname,
			search,
			locationDisabled,
			selectedState,
			selectedCity,
			workPlaces,
			educations,
			sectors,
			sponsors,
			supporters,
		],
	);

	const onFilterChange = useCallback(
		(name: 'workPlaces' | 'educations' | 'sectors' | 'sponsors' | 'supporters', value: string[]) => {
			clearTimeout(timeoutRef.current);

			timeoutRef.current = setTimeout(() => {
				navigateWithParams(
					pathname,
					{
						page: '1',
						workPlaces: name === 'workPlaces' ? value.join(',') : workPlaces.join(','),
						educations: name === 'educations' ? value.join(',') : educations.join(','),
						sectors: name === 'sectors' ? value.join(',') : sectors.join(','),
						sponsors: name === 'sponsors' ? value.join(',') : sponsors.join(','),
						supporters: name === 'supporters' ? value.join(',') : supporters.join(','),
					},
					'replace',
				);
			}, 500);
		},
		[educations, navigateWithParams, pathname, sectors, sponsors, supporters, workPlaces],
	);

	if (headerOnly) {
		return (
			<TopSection
				cities={cities}
				states={states}
				locationDisabled={locationDisabled}
				search={search}
				selectedCity={selectedCity}
				selectedState={selectedState}
				onSearch={onSearch}
				setSearch={setSearch}
				setSelectedCity={setSelectedCity}
				setSelectedState={value => {
					setSelectedCity(null);
					setSelectedState(value);
				}}
				showFilterButton={false}
				onFilterButtonPress={() => setFilterOpen(true)}
			/>
		);
	}

	return (
		<>
			<TopSection
				cities={cities}
				states={states}
				locationDisabled={locationDisabled}
				search={search}
				selectedCity={selectedCity}
				selectedState={selectedState}
				onSearch={onSearch}
				setSearch={setSearch}
				setSelectedCity={setSelectedCity}
				setSelectedState={value => {
					setSelectedCity(null);
					setSelectedState(value);
				}}
				onFilterButtonPress={() => setFilterOpen(true)}
			/>

			<BottomSection
				jobs={jobs}
				filterData={sponsorsProp}
				workPlaceOptions={workPlaceTypeOptions}
				educationOptions={educationOptions}
				sectorOptions={sectorsOptions.sort((a, b) => a.label.localeCompare(b.label))}
				filterOpen={filterOpen}
				educations={educations}
				sectors={sectors}
				workPlaces={workPlaces}
				sponsors={sponsors}
				supporters={supporters}
				setFilterOpen={setFilterOpen}
				page={page}
				pageMax={pageMax}
				onNextPage={() => navigateWithParams(pathname, { page: String(page + 1) }, 'replace', null, true)}
				onPreviousPage={() => navigateWithParams(pathname, { page: String(page - 1) }, 'replace', null, true)}
				setSponsors={value => {
					setSponsors(typeof value === 'string' ? [value] : value);

					onFilterChange('sponsors', typeof value === 'string' ? [value] : value);
				}}
				setSupporters={value => {
					setSupporters(typeof value === 'string' ? [value] : value);

					onFilterChange('supporters', typeof value === 'string' ? [value] : value);
				}}
				setEducations={value => {
					setEducations(value);

					onFilterChange('educations', value);
				}}
				setSectors={value => {
					setSectors(value);

					onFilterChange('sectors', value);
				}}
				setWorkPlaces={value => {
					setWorkPlaces(value);

					onFilterChange('workPlaces', value);
				}}
			/>
		</>
	);
};

export default Search;
